import { useState, type ReactNode } from "react";
import { PAGE_SIZE, type ListParams } from "../lib/useListParams";
import { loadedAt } from "../lib/format";
import { describe } from "../lib/advancedFilters";
import { ColumnChooser } from "./ColumnChooser";
import { AdvancedFilters } from "./AdvancedFilters";
import { Spinner } from "./Spinner";
import type { CsvColumns } from "../lib/columnChoice";
import { Button } from "../ui/Button";
import { FiltersPanel, Toolbar, useFiltersOpen, type Chip } from "../ui/Toolbar";

/* The chrome every list screen has, written once.
 *
 * Not an abstraction for its own sake. docs/plan.md lists the states every
 * screen has to build, and the reason they are here rather than in each screen
 * is that they have to be the same everywhere. Three copies of "what an empty
 * result says" is three chances for one of them to say "No results" and teach a
 * rep that the tool is broken.
 *
 * What each screen still owns: its columns, its filters, and its query. The
 * toolbar is src/ui/Toolbar.tsx, shared with the tables inside a record. */
export function ListShell({
  title,
  params,
  total,
  totalPending,
  rowCount,
  isLoading,
  isFetching,
  isError,
  error,
  dataUpdatedAt,
  onRefresh,
  onRetry,
  onDownload,
  csvColumns,
  searchLabel,
  filters,
  filterLabels = {},
  filterWords = {},
  view,
  emptyEverything,
  beforeList,
  lead,
  children,
}: {
  title: string;
  params: ListParams;
  total: number | null;
  /** True while the count is still being fetched. See the note on lastPage. */
  totalPending?: boolean;
  rowCount: number;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error: Error | null;
  dataUpdatedAt: number | undefined;
  onRefresh: () => void;
  onRetry: () => void;
  /** Fetches the whole narrowed set and downloads it. Returns a sentence to
   *  show when it could not give the person everything, and null when it did. */
  onDownload: () => Promise<string | null>;
  /** Which columns show in the table and go in the CSV, chosen by the person
   *  and remembered for them. */
  csvColumns?: CsvColumns;
  /* A list that names no label gets no search box. Jobs has no search behind
     it, and a control that does nothing is worse than no control.

     There is no placeholder prop and there is not going to be one. James,
     2026-09-22: the ambiguity is riskier than the blankness. A gray example
     scans as a box that already holds data, and the reader has to decide it
     is empty before reading anything else. The label says what the box is
     for. */
  searchLabel?: string;
  /** The screen's own filter controls, shown inside the filter panel. */
  filters?: ReactNode;
  /** What each named filter is called on its chip, by key. A key with no
   *  label is shown as the key with its underscores as spaces. */
  filterLabels?: Record<string, string>;
  /** How a named filter's value reads on its chip, by key, for a value that
   *  is not words, such as the campaigns list's owner. Without one the value
   *  is shown as it is. */
  filterWords?: Record<string, (value: string) => string>;
  /** The api view behind this list, for example "company". Given one, the
   *  screen offers stacked filters on any of its columns. */
  view?: string;
  /** What to say when nothing matched and nothing was being searched for. */
  emptyEverything: string;
  /** Rendered above the list and outside every state branch, so it survives
   *  loading, an empty result and an error. The campaign builder's criteria go
   *  here: they are what a person changes when the list is empty or slow, and
   *  putting them in children made them disappear at exactly those moments. */
  beforeList?: ReactNode;
  /** Rendered under the title and above the toolbar, for a screen whose form
   *  comes before its table: the builder's questions, so the table's
   *  controls sit with the table they act on. */
  lead?: ReactNode;
  children: ReactNode;
}) {
  /* The count is a separate request from the rows, because on the biggest
     views counting costs seconds and reading fifty rows costs milliseconds.
     Asking for both in one request made every screen wait for the slower half.
     So the list appears, and the total fills in behind it.
     Until it lands there is no last page to compute, and the rule that stands
     in for it is: a full page means there is probably another one. That can
     offer a Next on the true last page, which lands on an empty page and is
     recoverable, where a disabled Next on a page that does have more would
     strand somebody with no way to tell. */
  const [downloading, setDownloading] = useState(false);
  const [downloadSaid, setDownloadSaid] = useState<string | null>(null);
  const namedCount = Object.keys(params.filters).length;
  const [filtersOpen, setFiltersOpen] = useFiltersOpen(namedCount + params.advanced.length > 0);
  const countUnknown = total === null;
  const lastPage = countUnknown ? params.page : Math.max(1, Math.ceil(total / PAGE_SIZE));
  const canGoNext = countUnknown ? rowCount >= PAGE_SIZE : params.page < lastPage;
  const showPager = countUnknown ? params.page > 1 || canGoNext : lastPage > 1;
  const anyNarrowing = Boolean(params.search) || namedCount > 0 || params.advanced.length > 0;
  const firstRow = (params.page - 1) * PAGE_SIZE + 1;
  const hasFilters = Boolean(filters) || Boolean(view);

  const label = (key: string) => filterLabels[key] ?? key.replace(/_/g, " ");
  const chips: Chip[] = [
    ...Object.entries(params.filters).map(([key, value]) => ({
      label: `${label(key)}: ${filterWords[key]?.(value) ?? value}`,
      onRemove: () => params.set({ filters: { ...params.filters, [key]: "" } }),
    })),
    ...params.advanced.map((rule, i) => ({
      label: describe(view ?? "", rule),
      onRemove: () => params.set({ advanced: params.advanced.filter((_, n) => n !== i) }),
    })),
  ];

  return (
    <main>
      <h1>{title}</h1>

      {lead}

      <Toolbar
        {...(searchLabel ? { search: { label: searchLabel, value: params.search, onSubmit: (q: string) => params.set({ search: q }) } } : {})}
        filtersCount={namedCount + params.advanced.length}
        filtersOpen={filtersOpen}
        {...(hasFilters ? { onToggleFilters: () => setFiltersOpen((v) => !v) } : {})}
        chips={chips}
        onClearAll={() => params.set({ search: "", filters: {}, advanced: [] })}
        right={
          <>
            <span className="muted">{loadedAt(dataUpdatedAt)}</span>
            {csvColumns ? <ColumnChooser columns={csvColumns} /> : null}
            <Button
              size="compact-sm"
              onClick={async () => {
                setDownloading(true);
                setDownloadSaid(null);
                try {
                  setDownloadSaid(await onDownload());
                } catch (e) {
                  setDownloadSaid(e instanceof Error ? e.message : "the download did not work");
                } finally {
                  setDownloading(false);
                }
              }}
              disabled={rowCount === 0 || downloading}
            >
              {downloading ? "Preparing" : "Download CSV"}
            </Button>
            <Button size="compact-sm" onClick={onRefresh} disabled={isFetching}>
              {isFetching ? "Refreshing" : "Refresh"}
            </Button>
          </>
        }
      />

      {hasFilters ? (
        <FiltersPanel open={filtersOpen} onDone={() => setFiltersOpen(false)}>
          {filters ? <div className="filters-own">{filters}</div> : null}
          {view ? (
            <AdvancedFilters embedded view={view} rules={params.advanced}
                             onChange={(advanced) => params.set({ advanced })} />
          ) : null}
        </FiltersPanel>
      ) : null}

      {downloadSaid ? (
        <p className="refusal" role="status">
          {downloadSaid} <Button size="compact-sm" onClick={() => setDownloadSaid(null)}>Dismiss</Button>
        </p>
      ) : null}

      {beforeList}

      {isError ? (
        <p className="refusal" role="alert">
          {error?.message ?? "The request failed and gave no reason."}{" "}
          <Button size="compact-sm" onClick={onRetry}>Try again</Button>
        </p>
      ) : isLoading ? (
        /* Holds the layout so nothing shifts when the data lands. */
        <p className="muted" aria-live="polite"><Spinner />Loading.</p>
      ) : rowCount === 0 && !dataUpdatedAt ? (
        /* No rows and no successful fetch, ever. That is not an empty result
           and must never be reported as one.
           Reached on 2026-09-15 by putting a column the view does not have in
           the sort parameter: the API answered 400 twice, isError stayed false,
           and the screen said "No companies match these filters. Change them
           above" about filters that were fine. That is the same shape as the
           14 September failure, where a broken request read as an empty book.
           Why this guards on dataUpdatedAt rather than on isError: whatever
           swallowed the error, a screen that has never received an answer
           cannot honestly describe the book. */
        <p className="refusal" role="alert">
          This list could not be loaded.{" "}
          <Button size="compact-sm" onClick={onRetry}>Try again</Button>
        </p>
      ) : rowCount === 0 ? (
        <p className="refusal">
          {anyNarrowing ? (
            <>
              Nothing matches {params.search ? <>the name <strong>{params.search}</strong></> : "these filters"}
              {params.search && namedCount ? " with these filters" : ""}.{" "}
              <Button size="compact-sm" onClick={() => params.set({ search: "", filters: {}, advanced: [] })}>Clear and start again</Button>
            </>
          ) : (
            emptyEverything
          )}
        </p>
      ) : (
        <>
          <p className="muted" aria-live="polite">
            {total === null
              ? `Showing ${firstRow.toLocaleString()} to ${(firstRow + rowCount - 1).toLocaleString()}.`
              : `${total.toLocaleString()} match${total === 1 ? "" : "es"}. Showing ${firstRow.toLocaleString()} to ${(firstRow + rowCount - 1).toLocaleString()}.`}
            {/* The ring goes with the sentence that says a wait is on, so a
                slow count reads as running rather than as stopped. */}
            {total === null && totalPending ? <> <Spinner />Counting the rest.</> : null}
            {isFetching ? <> <Spinner />Refreshing.</> : null}
          </p>
          {children}
          {showPager ? (
            <div className="bar" style={{ marginTop: "1rem" }}>
              <Button onClick={() => params.set({ page: params.page - 1 })} disabled={params.page <= 1}>
                Previous
              </Button>
              <span className="muted">
                Page {params.page.toLocaleString()}
                {countUnknown ? "" : ` of ${lastPage.toLocaleString()}`}
              </span>
              <Button onClick={() => params.set({ page: params.page + 1 })} disabled={!canGoNext}>
                Next
              </Button>
            </div>
          ) : null}
        </>
      )}
    </main>
  );
}

/** A sortable column heading, for the lists not yet on the DataTable.
 *  Clicking it sorts, clicking again reverses, and a second column keeps
 *  the first as the tiebreak. */
export function SortHeader({
  column, label, params, numeric,
}: { column: string; label: string; params: ListParams; numeric?: boolean }) {
  const rank = params.sorts.findIndex((s) => s.column === column);
  const level = params.sorts[rank];
  const classes = [numeric ? "num" : "", rank === 0 ? "dt-sorted-primary" : rank > 0 ? "dt-sorted" : ""].filter(Boolean).join(" ");
  return (
    <th className={classes || undefined}
        aria-sort={rank === 0 && level ? (level.ascending ? "ascending" : "descending") : "none"}>
      <button
        className="sort"
        onClick={() => params.sortBy(column, { numeric: Boolean(numeric) })}
        title={rank > 0 ? "A tiebreak after the primary sort" : rank === 0 ? "The primary sort" : "Sort on this column"}
      >
        {label}
        {level ? <span className="dt-sort-mark" aria-hidden="true">{level.ascending ? " ↑" : " ↓"}</span> : null}
      </button>
    </th>
  );
}
